import { Flex } from "styled-system/jsx";
import { PageHeader, ViewSubSection } from "shared/components";
import { useTranslation } from "react-i18next";
import { List, WarningTriangle } from "iconoir-react";
import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { FormEvent } from "react";
import {
  HoursOfOperation,
  Seasonality,
  UpdateProfileFormValues,
  Keywords,
  KeyInfo,
  SocialLinks,
  DangerZone,
} from "apps/directory/Operator/views";
import { Amenities } from "./components/Amenities";
import { EnumOperatorType } from "gql/generated";
import { BusinessInfo } from "./components/BusinessInfo";
import { LocationContactInfo } from "./components/LocationContactInfo";

interface ProfileProps {
  form: UseFormReturn<UpdateProfileFormValues>;
  formId: string;
  onSubmit: (e?: FormEvent) => Promise<void>;
}

export const Profile = ({ form, formId, onSubmit }: ProfileProps) => {
  const { t } = useTranslation("directory", { keyPrefix: "OPERATOR.PROFILE" });

  const businessType = useWatch({
    control: form.control,
    name: "businessType",
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} id={formId}>
        <Flex flexDir={"column"} gap={"500"}>
          <PageHeader
            title={t("TITLE")}
            description={t("DESCRIPTION")}
            Icon={List}
          />
          {/* Key Info section: */}
          <ViewSubSection title={t("KEY_INFO.TITLE")}>
            <KeyInfo />
          </ViewSubSection>
          {/* Business Info section: */}
          <ViewSubSection title={t("BUSINESS_INFO.TITLE")}>
            <BusinessInfo />
          </ViewSubSection>
          {/* Keywords section: */}
          <ViewSubSection
            title={t("KEYWORDS.TITLE")}
            description={t("KEYWORDS.DESCRIPTION")}
          >
            <Keywords />
          </ViewSubSection>
          {/* Amenities section: */}
          {/* Only show amenities section for accommodation operators for now */}
          {businessType === EnumOperatorType.Accommodation && (
            <ViewSubSection
              title={t("AMENITY.TITLE")}
              description={t("AMENITY.DESCRIPTION")}
            >
              <Amenities />
            </ViewSubSection>
          )}
          {/* Location Contact Info section: */}
          <ViewSubSection title={t("LOCATION_CONTACT_INFO.TITLE")}>
            <LocationContactInfo />
          </ViewSubSection>
          {/* Social Links section: */}
          <ViewSubSection title={t("SOCIAL_LINKS.TITLE")}>
            <SocialLinks />
          </ViewSubSection>
          {/* Hours of Operation section: */}
          <ViewSubSection title={t("HOURS_OF_OPERATION.TITLE")}>
            <HoursOfOperation />
          </ViewSubSection>
          {/* Seasonality section: */}
          <ViewSubSection
            title={t("SEASONALITY.TITLE")}
            description={t("SEASONALITY.DESCRIPTION")}
          >
            <Seasonality />
          </ViewSubSection>
          {/* Danger Zone section: */}
          <ViewSubSection title={t("DANGER_ZONE.TITLE")} Icon={WarningTriangle}>
            <DangerZone />
          </ViewSubSection>
        </Flex>
      </form>
    </FormProvider>
  );
};
